import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import TextInput from './TextInput';
import RadioInput from './RadioInput';
import SelectInput from './SelectInput';
import TextAreaInput from './TextAreaInput';
import CheckboxInput from './CheckboxInput';
import FileInput from '../inputs/fileInput/FileInput';
import DateTimePickerInput from './DateTimePickerInput';

const InputComponents = {
  TextInput,
  DateTimePickerInput,
  SelectInput,
  TextAreaInput,
  CheckboxInput,
  RadioInput,
  FileInput,
};

FormInput.propTypes = {
  input: PropTypes.object,
  options: PropTypes.array,
  handleSelect: PropTypes.func,
  onChange: PropTypes.func,
  setSelectedVals: PropTypes.func,
  setFormData: PropTypes.func,
  removeAttachements: PropTypes.func,
  setHasSelectedSection: PropTypes.func,
};

export default function FormInput({
  input,
  options,
  handleSelect,
  onChange,
  setSelectedVals,
  setFormData,
  removeAttachements,
  setHasSelectedSection,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const Input = InputComponents[input.type];

  const validOptions = Array.isArray(options) ? options : [];

  const updatedOptions = [
    { value: '', text: t('pages.formPage.selectAnOption') },
    ...validOptions,
  ];

  // Assign props dynamically depending on input type
  const props = useMemo(() => {
    switch (input.type) {
      case 'TextInput':
        return {
          type: 'text',
          defaultValue: input.inputValue || '',
          label: input.text,
          fullWidth: true,
          onChange,
          size: isSmallScreen ? 'small' : 'medium',
          sx: {
            '& .MuiInputBase-input': { fontSize: isSmallScreen && '0.8rem' },
            '& .MuiInputLabel-root': { fontSize: isSmallScreen && '0.7rem' },
          },
        };
      case 'DateTimePickerInput':
        return {
          type: 'datetime',
          fullWidth: true,
          setSelectedVals,
          defaultValue: input.inputValue || '',
          size: isSmallScreen ? 'small' : 'medium',
          sx: {
            '& .MuiInputBase-input': { fontSize: isSmallScreen && '0.8rem' },
            '& .MuiInputLabel-root': { fontSize: isSmallScreen && '0.7rem' },
          },
        };
      case 'SelectInput':
        return {
          type: 'select',
          options: updatedOptions,
          setSelectedInput: (value) => {
            if (value === '') {
              handleSelect(null);
            } else {
              handleSelect(value);
            }
          },
          fullWidth: true,
          defaultValue: input.inputValue || '',
          labelValue: input.text,
          size: isSmallScreen ? 'small' : 'medium',
          sx: {
            '& .MuiInputBase-input': { fontSize: isSmallScreen && '0.8rem' },
            '& .MuiInputLabel-root': { fontSize: isSmallScreen && '0.7rem' },
          },
          setHasSelectedSection,
          input,
        };
      case 'TextAreaInput':
        return {
          type: 'textarea',
          fullWidth: true,
          defaultValue: input.inputValue || '',
          onChange,
          size: isSmallScreen ? 'small' : 'medium',
          sx: {
            '& .MuiInputBase-input': { fontSize: isSmallScreen && '0.8rem' },
            '& .MuiInputLabel-root': { fontSize: isSmallScreen && '0.7rem' },
          },
        };
      case 'CheckboxInput':
        return {
          type: 'Checkbox',
          options,
          defaultValue: input.inputValue || [],
          setSelectedVals,
          size: isSmallScreen ? 'small' : 'medium',
          sx: {
            '& .MuiFormControlLabel-label': { fontSize: isSmallScreen && '0.7rem' },
            '& .MuiCheckbox-root': { transform: isSmallScreen && 'scale(0.8)' },
          },
        };
      case 'RadioInput':
        return {
          type: 'radio',
          options,
          defaultValue: input.inputValue || '',
          setSelectedInput: onChange,
          isRequired: input.isRequired,
          setHasSelectedSection,
          size: isSmallScreen ? 'small' : 'medium',
          sx: {
            '& .MuiFormControlLabel-label': { fontSize: isSmallScreen && '0.7rem' },
            '& .MuiRadio-root': { transform: isSmallScreen && 'scale(0.8)' },
          },
          input,
        };
      case 'FileInput':
        return {
          type: 'fileInput',
          setFilesList: setSelectedVals,
          setFormData,
          removeAttachements,
          size: isSmallScreen ? 'small' : 'medium',
          sx: {
            '& .MuiInputBase-input': { fontSize: isSmallScreen ? '0.8rem' : '1rem' },
            '& .MuiInputLabel-root': { fontSize: isSmallScreen ? '0.7rem' : '0.9rem' },
          },
        };
      default:
        return {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, options, t]);

  return <Input {...props} required={input.isRequired} />;
}
